"use client";

import { useEffect, useState } from "react";
import { Scrollable } from "@/components/scrollable";
import { ProductCard } from "@/components/product-card";
import { getProductList } from "@/actions/products";
import {
  Product,
  Status,
  type GetProductListOutput,
} from "@/actions/schemas/products";
import { storeAnalitica } from "@/app/(store)/_utils/events";
import { useEffectOnce } from "@/hooks/use-effect-once";
import { useFilters } from "@/contexts/filters-provider";
import { Feedback } from "@/components/feedback";
import { type OrderOption } from "@/app/(store)/_components/order-dropdown/order-options";
import { useExperiment } from "@/contexts/analitica";
import { ExperimentsFlags } from "@/services/amplitude/experiment";
import { FloatingCartBar } from "@/components";
import { getProductStore, useOrderResumeStore } from "@/stores";
import { useVitrineStore } from "@/contexts/vitrine-provider";
import { useIsStoreOpen } from "@/hooks/use-is-store-open";

type ProductLoadingProps = {
  items?: number;
};

type Props = {
  products: GetProductListOutput;
  filters: {
    category?: string | null;
    term?: string | null;
    order?: OrderOption;
  };
};

const ProductSkeleton = ({ items }: { items: number }) => {
  return (
    <>
      {[...Array(items).keys()].map((key, index) => (
        <ProductCard
          hasBorder={index !== items - 1}
          loading={true}
          key={`skeleton-${key}`}
        />
      ))}
    </>
  );
};

export const ProductLoading = ({ items = 9 }: ProductLoadingProps) => {
  return (
    <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3">
      <ProductSkeleton items={items} />
    </div>
  );
};

export const ProductsList = ({ products: initialProducts, filters }: Props) => {
  const { summary, toggleDrawer, upsertItemFromProduct } =
    useOrderResumeStore();

  const { isOrdersEnabled, store } = useVitrineStore((state) => state);
  const { isOpen: isStoreOpen, flagOn: isStoreOpenFlagOn } = useIsStoreOpen();

  const { isFiltering, setIsFiltering } = useFilters();

  const [isFetching, setIsFetching] = useState<boolean>(false);
  const [products, setProducts] = useState<Product[]>(
    initialProducts?.items || []
  );
  const [nextCursor, setNextCursor] = useState<string | null>(
    initialProducts?.nextPageCursor || null
  );
  const [showCartBar, setShowCartBar] = useState<boolean>(false);

  const showCartExperiment = useExperiment(ExperimentsFlags.ShowCart);

  useEffectOnce(() => {
    const hasProducts = initialProducts?.items?.length > 0;

    if (hasProducts) {
      return storeAnalitica.events.view.pageViewed(
        !!filters.category || !!filters.term,
        isOrdersEnabled
      );
    }

    if (filters.term) {
      return storeAnalitica.events.view.searchedAndNotFound();
    }
  });

  useEffect(() => {
    setIsFetching(true);
    setIsFiltering(false);
    setProducts(initialProducts.items);
    setNextCursor(initialProducts.nextPageCursor || null);
    setIsFetching(false);
  }, [initialProducts, setIsFiltering]);

  useEffect(() => {
    if (!summary.itemsCount) {
      setShowCartBar(false);
    }
  }, [summary.itemsCount]);

  const fetchProducts = async (cursor?: string) => {
    if (!store.id) return;

    setIsFetching(true);

    const result = await getProductList({
      storeId: store.id,
      status: Status.Public,
      productName: filters.term ? decodeURI(filters.term as string) : "",
      categoryId: (filters.category as string) || "",
      orderBy: filters.order?.orderBy,
      orderDirection: filters.order?.orderDirection,
      cursor: (cursor as string) || "",
    });

    if (result.error) {
      throw new Error("Erro ao buscar produtos");
    }

    const { items, nextPageCursor } = result.data;

    setProducts((prev) => {
      return cursor ? [...prev, ...items] : items;
    });
    setNextCursor(nextPageCursor || null);
    setIsFetching(false);
  };

  const getMoreProducts = async () => {
    if (isFetching || !nextCursor) return;

    await fetchProducts(nextCursor);
  };

  const onOpenCart = () => {
    setShowCartBar(false);
    toggleDrawer();
  };

  const addToCart = (product: Product) => {
    const productStore = getProductStore(product);
    const chosenVariants = Object.values(productStore.chosenVariants) || [];

    upsertItemFromProduct({
      id: product.variants[0].id,
      productName: product.name,
      productImage: product.images?.[0]?.url,
      price: productStore.price!,
      quantity: productStore.quantity,
      chosenVariants,
    });

    setShowCartBar(true);
  };

  if (isFiltering && !isFetching) {
    return <ProductLoading />;
  }

  const isShowCartExperiment =
    showCartExperiment && showCartExperiment?.key === "on";

  const isStoreClosed = isStoreOpenFlagOn && !isStoreOpen;

  return (
    <div className="flex w-full flex-1 flex-col" id="products-list">
      {isShowCartExperiment && (
        <section aria-label="Ações">
          <FloatingCartBar
            open={showCartBar}
            quantity={summary.itemsCount}
            price={summary.subtotal}
            onOpenCart={onOpenCart}
          />
        </section>
      )}
      {products.length === 0 ? (
        <Feedback
          icon="action-search"
          title="Não encontramos nenhum produto"
          message=""
          data-testid="section-products-empty-state"
        />
      ) : (
        <Scrollable
          className="grid w-full grid-cols-1 items-start gap-4 md:grid-cols-2 md:items-stretch md:gap-x-5 md:gap-y-4 xl:grid-cols-3 xl:gap-5"
          loading={isFetching}
          loader={<ProductSkeleton items={6} />}
          hasMore={!!nextCursor}
          onLoadMore={getMoreProducts}
          dataTestId="section-products-list"
        >
          {products.map((product) => (
            <ProductCard
              store={store}
              data={product}
              onClick={() => storeAnalitica.events.action.productClicked()}
              addToCart={addToCart}
              showCartButton={
                isOrdersEnabled && isShowCartExperiment && !isStoreClosed
              }
              key={product.id}
            />
          ))}
        </Scrollable>
      )}
    </div>
  );
};
